import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import PageLoader from "../../components/common/PageLoader";
import api from '../../services/api';
import Swal from 'sweetalert2';
import './AllowanceSettings.css';

const TYPE_OPTIONS = ['Allowances', 'Deductions'];

const BENEFIT_TITLE_OPTIONS = [
  'House Rent Allowance',
  'Travel Allowance',
  'Conveyance Allowance',
  'Medical Allowance',
  'Food Allowance',
  'City Compensatory Allowance',
  'Special Allowance',
];

const EMPTY_ROW = {
  title: '',
  type: '',
  tax_regime: '',
  city_type: '',
  value_in_percent: 0,
  benefits_on: '',
};

const HRA_TITLE = 'House Rent Allowance';

const isHraTitle = (title) => title === HRA_TITLE;
const getHraPercent = (cityType) => (String(cityType) === '1' ? 50 : 40);
const getCityTypeLabel = (value) => (Number.parseInt(value, 10) === 1 ? 'Metro' : 'Non Metro');
const sortFinancialYears = (years) => [...years].sort((a, b) => Number(b) - Number(a));
const formatVersionDate = (value) => {
  if (!value) {
    return 'Version date unavailable';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Version date unavailable';
  }

  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};
const sortVersions = (versions = []) =>
  [...versions].sort((a, b) => {
    if (Number(b.is_latest) !== Number(a.is_latest)) {
      return Number(b.is_latest) - Number(a.is_latest);
    }

    return Number(b.version_number) - Number(a.version_number);
  });

function AllowanceSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [submitting, setSubmitting] = useState(false);
  const [flashMsg, setFlashMsg] = useState(null);

  const financialYear = searchParams.get('financial_year');
  const versionNumber = searchParams.get('version_number');
  const addMode = searchParams.get('add') === '1';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (financialYear) params.financial_year = financialYear;
      if (versionNumber) params.version_number = versionNumber;
      if (addMode) params.add = '1';

      const res = await api.get('/benefits-settings', { params });

      if (res.data.success) {
        const responseData = res.data.data;
        setData(responseData);

        if (addMode && responseData.benefits?.length) {
          setRows(
            responseData.benefits.map((benefit) => ({
              title: benefit.title || '',
              type: benefit.type || '',
              tax_regime: benefit.tax_regime || '',
              city_type: String(benefit.city_type ?? ''),
              value_in_percent: benefit.value_in_percent ?? 0,
              benefits_on: benefit.benefits_on || '',
            })),
          );
        } else if (addMode) {
          setRows([{ ...EMPTY_ROW }]);
        }
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load allowance settings',
      });
    } finally {
      setLoading(false);
    }
  }, [financialYear, versionNumber, addMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToYear = () => setSearchParams({});
  const goToVersions = (year) => setSearchParams({ financial_year: year });
  const goToVersion = (year, version) =>
    setSearchParams({ financial_year: year, version_number: version });
  const goToAddMode = (year) => setSearchParams({ financial_year: year, add: '1' });

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const getTitleOptions = (selectedTitle) => {
    const options = [...BENEFIT_TITLE_OPTIONS];
    if (selectedTitle && !options.includes(selectedTitle)) {
      options.unshift(selectedTitle);
    }
    return options;
  };

  const updateRow = (rowIndex, field, value) => {
    setRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        if (field === 'title') {
          if (isHraTitle(value)) {
            const nextCityType = row.city_type === '' ? '0' : row.city_type;
            return {
              ...row,
              title: value,
              city_type: nextCityType,
              value_in_percent: getHraPercent(nextCityType),
            };
          }

          return {
            ...row,
            title: value,
            city_type: '',
          };
        }

        if (field === 'city_type') {
          return {
            ...row,
            city_type: value,
            value_in_percent: isHraTitle(row.title)
              ? getHraPercent(value)
              : row.value_in_percent,
          };
        }

        if (field === 'value_in_percent' && isHraTitle(row.title)) {
          return {
            ...row,
            value_in_percent: value,
            city_type: String(value) === '50' ? '1' : '0',
          };
        }

        return { ...row, [field]: value };
      }),
    );
  };

  const removeRow = (rowIndex) => {
    setRows((prev) => prev.filter((_, index) => index !== rowIndex));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const hasErrors = rows.some(
      (row) =>
        !row.title.trim() ||
        !row.type ||
        !row.tax_regime ||
        row.city_type === '' ||
        !row.benefits_on ||
        row.value_in_percent === '' ||
        parseFloat(row.value_in_percent) < 0,
    );

    if (hasErrors) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill all required fields in every row.',
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        financial_year: parseInt(financialYear, 10),
        benefits: rows.map((row) => ({
          title: row.title.trim(),
          type: row.type,
          tax_regime: row.tax_regime,
          city_type: row.city_type,
          value_in_percent: parseFloat(row.value_in_percent),
          benefits_on: row.benefits_on,
        })),
      };

      const res = await api.post('/benefits-settings', payload);

      if (res.data.success !== false) {
        setFlashMsg({
          type: 'success',
          text: res.data.message || 'Allowance settings saved successfully.',
        });
        goToVersions(financialYear);
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const messages = Object.values(errors).flat().join(' | ');
        setFlashMsg({ type: 'error', text: messages });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Failed to save allowance settings.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const versions = sortVersions(data?.versions || []);
  const currentFinancialYear = data?.current_financial_year;
  const latestVersion = versions.find((version) => Number(version.is_latest) === 1) || versions[0] || null;
  const totalAllowanceItems = data?.benefits?.length || latestVersion?.benefits_count || 0;

  const Breadcrumb = () => (
    <nav className="bs-breadcrumb" aria-label="breadcrumb">
      {!financialYear ? (
        <span className="bs-crumb active">Financial Year</span>
      ) : (
        <button className="bs-crumb link" onClick={goToYear}>
          Financial Year
        </button>
      )}

      {financialYear && (
        <>
          <span className="bs-crumb-sep">&gt;</span>
          {!addMode && !versionNumber ? (
            <span className="bs-crumb active">FYE {data?.financial_year_label || financialYear}</span>
          ) : (
            <button className="bs-crumb link" onClick={() => goToVersions(financialYear)}>
              FYE {data?.financial_year_label || financialYear}
            </button>
          )}
        </>
      )}

      {versionNumber && (
        <>
          <span className="bs-crumb-sep">&gt;</span>
          <span className="bs-crumb active">
            {versions.find((version) => String(version.version_number) === String(versionNumber))
              ?.version_label || `V${versionNumber}`}
          </span>
        </>
      )}

      {addMode && !versionNumber && (
        <>
          <span className="bs-crumb-sep">&gt;</span>
          <span className="bs-crumb active">Add Allowance</span>
        </>
      )}
    </nav>
  );

  const AllowanceTable = ({ benefits }) => (
    <div className="table-responsive">
      <table className="bs-table">
        <thead>
          <tr>
            {['Title', 'Type', 'Tax Regime', 'City Type', 'Value %', 'Allowance On'].map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!benefits?.length ? (
            <tr>
              <td colSpan="6" className="empty-cell">
                No allowances in this version.
              </td>
            </tr>
          ) : (
            benefits.map((benefit, index) => (
              <tr key={index}>
                <td>{benefit.title}</td>
                <td>
                  <span className="type-badge">{benefit.type}</span>
                </td>
                <td>{benefit.tax_regime}</td>
                <td>{Number.parseInt(benefit.city_type, 10) === 1 ? 'Metro' : 'Non Metro'}</td>
                <td>{benefit.value_in_percent}%</td>
                <td>{benefit.benefits_on}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading allowance settings..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="benefits-settings-page">
        {flashMsg && (
          <div className={`bs-alert bs-alert-${flashMsg.type}`}>
            {flashMsg.text}
            <button className="bs-alert-close" onClick={() => setFlashMsg(null)}>
              x
            </button>
          </div>
        )}

        <div className="bs-breadcrumb-card">
          <Breadcrumb />
        </div>

        {!financialYear && (
          <div className="bs-card">
            <div className="bs-card-body">
              <h4>Allowance Settings</h4>
              <p className="bs-subtitle">Choose a financial year.</p>

              <div className="bs-year-grid">
                {sortFinancialYears(Object.values(data?.year_list || {})).map((year) => {
                  const isCurrentYear = String(currentFinancialYear) === String(year);
                  return (
                    <button
                      key={year}
                      className={`bs-year-card ${isCurrentYear ? 'current' : 'archived'}`}
                      onClick={() => goToVersions(year)}
                    >
                      <div className="bs-year-top">
                        <span className="bs-year-icon">
                          <i className="fas fa-calendar-alt"></i>
                        </span>
                        {isCurrentYear ? (
                          <span className="badge-latest">Current</span>
                        ) : (
                          <span className="badge-archive">Archive</span>
                        )}
                      </div>
                      <span className="bs-year-label">{year - 1} - {year}</span>
                      <p className="bs-year-copy">
                        Review allowance versions and create a fresh snapshot for this FY.
                      </p>
                      <span className="bs-year-sub">View versions</span>
                      <span className="bs-card-arrow">
                        <i className="fas fa-arrow-right"></i>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {financialYear && !addMode && !versionNumber && (
          <div className="bs-card">
            <div className="bs-card-body">
              <div className="bs-card-header-row">
                <div>
                  <h4>FYE {data?.financial_year_label}</h4>
                  <p className="bs-subtitle">Your allowance versions for this financial year.</p>
                </div>
                <button className="btn-primary-sm" onClick={() => goToAddMode(financialYear)}>
                  + Create New Version
                </button>
              </div>

              <div className="bs-card-body bs-summary-strip">
                <div className="bs-summary-tile">
                  <span>Latest version</span>
                  <strong>{latestVersion?.version_label || 'Not created yet'}</strong>
                </div>
                <div className="bs-summary-tile">
                  <span>Total versions</span>
                  <strong>{versions.length}</strong>
                </div>
                <div className="bs-summary-tile">
                  <span>Latest snapshot items</span>
                  <strong>{latestVersion?.benefits_count || 0}</strong>
                </div>
              </div>

              {!versions.length ? (
                <p className="bs-empty-text">
                  No allowance versions yet. Click "Create New Version" to create the first one.
                </p>
              ) : (
                <div className="bs-version-grid">
                  {versions.map((version) => (
                    <button
                      key={version.version_number}
                      className={`bs-version-card ${version.is_latest === '1' ? 'current' : 'archived'}`}
                      onClick={() => goToVersion(financialYear, version.version_number)}
                    >
                      <div className="bs-version-top">
                        <span className="bs-version-icon">
                          <i className="fas fa-layer-group"></i>
                        </span>
                        {version.is_latest === '1' ? (
                          <span className="badge-latest">Latest</span>
                        ) : (
                          <span className="badge-archive">Archive</span>
                        )}
                      </div>
                      <span className="bs-version-name">{version.version_label}</span>
                      <p className="bs-version-date">{formatVersionDate(version.created_at)}</p>
                      <p className="bs-version-status">
                        {version.is_latest === '1'
                          ? 'Current active version'
                          : 'Previous archived version'}
                      </p>
                      <p className="bs-version-count">{version.benefits_count} item(s)</p>
                      <span className="bs-card-arrow">
                        <i className="fas fa-arrow-right"></i>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {financialYear && versionNumber && (() => {
          const selectedVersion = versions.find(
            (version) => String(version.version_number) === String(versionNumber),
          );

          return (
            <div className="bs-card">
              <div className="bs-card-header">
                <div className="bs-card-header-row">
                  <h5 className="bs-version-title">
                    {selectedVersion?.version_label || `V${versionNumber}`}
                    <span className="badge-count">{data?.benefits?.length || 0}</span>
                    <span>FYE {data?.financial_year_label}</span>
                    {selectedVersion?.is_latest === '1' && (
                      <span className="badge-latest">Latest</span>
                    )}
                  </h5>
                  <button className="btn-primary-sm" onClick={() => goToAddMode(financialYear)}>
                    + Create New Version
                  </button>
                </div>
              </div>
              <div className="bs-card-body">
                <AllowanceTable benefits={data?.benefits} />
              </div>
            </div>
          );
        })()}

        {financialYear && addMode && (
          <>
            {data?.benefits?.length > 0 && (
              <div className="bs-card bs-ref-card">
                <div className="bs-card-header">
                  <h5>
                    Latest Allowance Version
                    <span className="badge-count">{data.benefits.length}</span>
                  </h5>
                </div>
                <div className="bs-card-body">
                  <AllowanceTable benefits={data.benefits} />
                </div>
              </div>
            )}

            <div className="bs-card">
              <div className="bs-card-header">
                <div className="bs-card-header-row">
                  <div>
                    <h5>Create Allowance Version</h5>
                    <p className="bs-subtitle">
                      This form is prefilled from the latest version. Saving will create a new snapshot for FYE {data?.financial_year_label}.
                    </p>
                  </div>
                  <button type="button" className="btn-primary-sm" onClick={addRow}>
                    + Add Row
                  </button>
                </div>
              </div>

              <div className="bs-card-body">
                <div className="bs-version-hero">
                  <div className="bs-version-hero-copy">
                    <span className="bs-version-hero-label">New version snapshot</span>
                    <h6>Build the next live allowance setup</h6>
                    <p>
                      Create a fresh version instead of editing history. Your previous versions stay locked and readable.
                    </p>
                  </div>
                  <div className="bs-version-hero-metrics">
                    <div>
                      <span>FY</span>
                      <strong>{data?.financial_year_label}</strong>
                    </div>
                    <div>
                      <span>Prefilled items</span>
                      <strong>{totalAllowanceItems}</strong>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="table-responsive">
                    <table className="bs-table bs-editable-table">
                      <thead>
                        <tr>
                          <th>Title <span className="req">*</span></th>
                          <th>Type <span className="req">*</span></th>
                          <th>Tax Regime <span className="req">*</span></th>
                          <th>City Type <span className="req">*</span></th>
                          <th>Value % <span className="req">*</span></th>
                          <th>Allowance On <span className="req">*</span></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={index} className="editable-row">
                            <td>
                              <select
                                className="fc"
                                value={row.title}
                                onChange={(event) => updateRow(index, 'title', event.target.value)}
                              >
                                <option value="">- Select -</option>
                                {getTitleOptions(row.title).map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="fc"
                                value={row.type}
                                onChange={(event) => updateRow(index, 'type', event.target.value)}
                              >
                                <option value="">- Select -</option>
                                {TYPE_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="fc"
                                value={row.tax_regime}
                                onChange={(event) => updateRow(index, 'tax_regime', event.target.value)}
                              >
                                <option value="">- Select -</option>
                                <option value="Old">Old</option>
                                <option value="New">New</option>
                              </select>
                            </td>
                            <td>
                              <div className="bs-cell-stack">
                                <select
                                  className="fc"
                                  value={row.city_type}
                                  onChange={(event) => updateRow(index, 'city_type', event.target.value)}
                                >
                                  <option value="">- Select -</option>
                                  <option value="0">Non Metro</option>
                                  <option value="1">Metro</option>
                                </select>
                                {isHraTitle(row.title) ? (
                                  <p className="bs-inline-note">
                                    {row.city_type === ''
                                      ? 'Choose metro or non-metro to apply the correct HRA percentage.'
                                      : `${getCityTypeLabel(row.city_type)} cities use ${getHraPercent(row.city_type)}% HRA.`}
                                  </p>
                                ) : (
                                  <p className="bs-inline-note muted">
                                    Keep city type aligned with the employee location rule for this allowance.
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="bs-cell-stack">
                                {isHraTitle(row.title) ? (
                                  <select
                                    className="fc fc-num"
                                    value={row.value_in_percent}
                                    onChange={(event) =>
                                      updateRow(index, 'value_in_percent', event.target.value)
                                    }
                                  >
                                    <option value="40">40%</option>
                                    <option value="50">50%</option>
                                  </select>
                                ) : (
                                  <input
                                    type="number"
                                    className="fc fc-num"
                                    step="0.01"
                                    min="0"
                                    value={row.value_in_percent}
                                    onChange={(event) =>
                                      updateRow(index, 'value_in_percent', event.target.value)
                                    }
                                  />
                                )}
                                {isHraTitle(row.title) && (
                                  <p className="bs-inline-note accent">
                                    HRA is restricted to 40% for non-metro and 50% for metro.
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>
                              <select
                                className="fc"
                                value={row.benefits_on}
                                onChange={(event) => updateRow(index, 'benefits_on', event.target.value)}
                              >
                                <option value="">- Select -</option>
                                <option value="Basic">Basic</option>
                                <option value="Gross">Gross</option>
                              </select>
                            </td>
                            <td>
                              {rows.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-remove-row"
                                  onClick={() => removeRow(index)}
                                  title="Remove row"
                                >
                                  x
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bs-form-footer">
                    <button type="submit" className="btn-submit" disabled={submitting}>
                      {submitting ? 'Saving...' : 'Save Allowance'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default AllowanceSettings;
